import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardService } from '../../dashboard.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { ToastrService } from 'ngx-toastr';
import { helper } from 'src/environments/helper';
import { environment } from 'src/environments/environment';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-upload-anexe',
    templateUrl: './upload-anexe.component.html',
    styleUrls: ['./upload-anexe.component.scss'],
})
export class UploadAnexeComponent implements OnInit {
    // Toast config
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;

    // File validation
    allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/png',
        'image/jpeg',
        'image/jpg'
    ];
    allowedExtensions = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg';
    maxFileSize = 10 * 1024 * 1024; // 10 MB

    // State
    uploadedFiles: any[] = [];
    uploading: boolean = false;
    isLoading: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            documentGeneratId: number;
            existingAnexe: any[];
            canDelete: boolean;
        },
        private dialogRef: MatDialogRef<UploadAnexeComponent>,
        private dashboardService: DashboardService,
        private notificationService: NotificationService,
        private appFilesService: AppFilesService,
        public toastr: ToastrService
    ) {}

    ngOnInit(): void {
        // Filter existing anexe to show only active ones
        if (this.data.existingAnexe) {
            this.data.existingAnexe = this.data.existingAnexe.filter((a: any) => a.active === 1 || a.active === true);
        }
    }

    closeDialog(): void {
        this.dialogRef?.close({
            success: false,
        });
    }

    /**
     * Validate a single file
     */
    validateFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
            errors.push(`Tipul de fișier "${file.type}" nu este permis. Tipuri acceptate: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG.`);
        }

        // Check file size
        if (file.size > this.maxFileSize) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
            errors.push(`Fișierul "${file.name}" (${sizeMB} MB) depășește limita de 10 MB.`);
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Handle file selection from input
     */
    addFile(event: any): void {
        event.preventDefault();
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files || inputElement.files.length === 0) {
            return;
        }

        const files = inputElement.files;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Validate file
            const validation = this.validateFile(file);
            if (!validation.isValid) {
                validation.errors.forEach(error => {
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        error,
                        this.errorIcon
                    );
                });
                continue;
            }

            this.uploading = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.dashboardService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    if (result && result.status_code === 200 && result.data) {
                        this.uploadedFiles.push(result.data);
                        this.uploading = false;
                        this.openToast({
                            title: this.successTitle,
                            message: `Fișierul "${file.name}" a fost adăugat cu succes.`,
                            type: this.successIcon,
                        });
                    } else {
                        this.uploading = false;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            'Eroare la încărcarea fișierului.',
                            this.errorIcon
                        );
                    }
                },
                error: (error: any) => {
                    this.uploading = false;
                    this.onHttpError(error);
                },
            });
        }

        // Reset input
        inputElement.value = '';
    }

    /**
     * Remove a file from the upload list (before submit)
     */
    removeFile(index: number): void {
        if (index > -1 && index < this.uploadedFiles.length) {
            this.uploadedFiles.splice(index, 1);
        }
    }

    /**
     * View an uploaded file (before submit) - uses temp file endpoint
     */
    viewUploadedFile(file: any): void {
        const fullPath = this.dashboardService.tempFileDownloadUrl;
        const payload = {
            fileName: file.filename,
            originalName: file.original_name,
        };
        this.isLoading = true;
        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('View file error: ', err);
                this.isLoading = false;
                this.notificationService.warningSwal(
                    this.errorTitle,
                    'Eroare la vizualizarea fișierului.',
                    this.errorIcon
                );
            });
    }

    /**
     * View an existing anexa (already saved in DB) - uses document download endpoint
     */
    viewExistingAnexa(anexa: any): void {
        const fullPath = this.dashboardService.documentDownloadFileUrl + anexa.id;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('View anexa error: ', err);
                this.isLoading = false;
                this.notificationService.warningSwal(
                    this.errorTitle,
                    'Eroare la vizualizarea anexei.',
                    this.errorIcon
                );
            });
    }

    /**
     * Delete an existing anexa (already saved in DB)
     */
    deleteExistingAnexa(anexa: any): void {
        if (!this.data.canDelete) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Nu se pot șterge anexe după ce notificarea a fost trimisă către cetățean.',
                this.errorIcon
            );
            return;
        }

        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: `Sigur doriți să ștergeți anexa "${anexa.old_name}"?`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
            preConfirm: () => {
                return this.dashboardService
                    .deleteDocumentGeneratAnexa(anexa.id)
                    .toPromise()
                    .then((response) => response)
                    .catch((err) => {
                        let msg = err?.error?.error || err?.error?.message || helper.dialogConfig.generalMessages.error;
                        Swal.showValidationMessage(`${msg}`);
                    });
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Remove from local list
                const index = this.data.existingAnexe.findIndex((a: any) => a.id === anexa.id);
                if (index > -1) {
                    this.data.existingAnexe.splice(index, 1);
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Anexa a fost ștearsă cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    /**
     * Submit all uploaded files to backend
     */
    submitAnexe(): void {
        if (this.uploadedFiles.length === 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Va rugăm să încărcați cel puțin un fișier.',
                this.errorIcon
            );
            return;
        }

        this.isLoading = true;

        const payload = {
            files: this.uploadedFiles
        };

        this.dashboardService.uploadDocumentGeneratAnexe(this.data.documentGeneratId, payload)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (res: any) => {
                    this.openToast({
                        title: this.successTitle,
                        message: 'Anexele au fost încărcate cu succes.',
                        type: this.successIcon,
                    });
                    this.dialogRef.close({ success: true });
                },
                error: (err: any) => {
                    let errorMessage = err?.error?.error || err?.error?.message || 'Eroare la încărcarea anexelor.';
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        errorMessage,
                        this.errorIcon
                    );
                }
            });
    }

    /**
     * Handle HTTP errors
     */
    onHttpError(res: any): void {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    msg,
                    this.errorIcon
                );
            });
        } else {
            let errorMessage = environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }

    /**
     * Show toast notification
     */
    openToast(object: any): void {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }
}
